<template>
  <t-drawer
    v-model:visible="visible"
    :footer="false"
    size="450px"
    class="edit-node-compo"
    prevent-scroll-through
    show-in-attached-element
    :show-overlay="true"
    @close="handlerClose"
  >
    <template #header>
      <header class="header">
        <div class="icon">
          <img :src="nodeCfg?.icon" :alt="nodeCfg?.type" />
        </div>
        <div class="title-editor">
          <span class="label"> {{ nodeCfg.group }} </span>
        </div>
        <div class="title-operation">
          <CloseCircleIcon size="24" @click="handlerClose" />
        </div>
      </header>
    </template>

    <section>
      <t-form ref="formRef" label-align="top" :data="formData">
        <t-form-item
          :rules="requireRule"
          :label="$t('节点名称')"
          name="nodeName"
          :required-mark="false"
        >
          <t-input v-model="formData.nodeName" maxlength="100" />
        </t-form-item>

        <!-- 根据节点类型显示不同的表单 -->
        <normal-task-form
          v-if="isNormalTask"
          :node-cfg="nodeCfg"
          :model-data="modelData"
          :form-data="formData"
          :visible="visible"
          @update:formData="handleFormDataUpdate"
        />

        <agent-task-form
          v-if="isAgentTask"
          :node-cfg="nodeCfg"
          :model-data="modelData"
          :form-data="formData"
          :visible="visible"
          @update:formData="handleFormDataUpdate"
        />
      </t-form>
    </section>
  </t-drawer>
</template>

<script lang="ts" setup>
import { nextTick, reactive, ref, watch, computed } from "vue";
import { FormInstanceFunctions } from "tdesign-vue-next";
import { CloseCircleIcon } from "tdesign-icons-vue-next";
import { mergeWith, cloneDeep, trim, isArray } from "lodash-es";
import { formatXGraphLabel } from "@/utils/xgraph-util";
import { getRelationUUID } from "@/utils";
import { t } from "@/utils/i18n";
import NormalTaskForm from "./normal-task-form.vue";
import AgentTaskForm from "./agent-task-form.vue";

const emit = defineEmits(["submit", "close"]);

const props = defineProps<{
  nodeList: Array<{
    group: string;
    type: string;
    icon: string;
    pIcon: string;
    canEditRelation?: boolean;
  }>;
}>();

type Model = {
  id: string;
  label: string;
  nodeData: {
    nodeName: string;
    nodeType: string;
    branches: Array<{
      content?: string;
      id: string;
      condition?: Array<any>;
      type?: 1 | 2;
      urlStatus?: 0 | 1;
      url?: string;
    }>;
  };
  type: string;
  // x: number;
  // y: number;
};

const formRef = ref<FormInstanceFunctions>(null as any);
const visible = ref(false);

const modelData = ref<Model>({
  id: "",
  label: "",
  nodeData: {
    nodeName: "",
    nodeType: "",
    branches: [],
  },
  type: "",
});

const nodeCfg = computed(() => {
  const nodeType = modelData.value.nodeData.nodeType;
  return (
    props.nodeList.find((v) => {
      return v.type === nodeType;
    }) || props.nodeList[0]
  );
});

const formData = reactive<{
  branches: Model["nodeData"]["branches"];
  nodeName: string;
  intentions: string[];
  desc: string;
  content: string;
  type: "1" | "2";
  tip: string;
  optionType: number;
  routePolicy: string;
  groupId?: undefined;
  url?: string;
  // 标准任务流字段
  request?: {
    body: Array<{
      varType: number;
      key: string;
      value: string;
      valueType: string;
    }>;
  };
  response?: {
    body: Array<{
      key: string;
      valueType: string;
    }>;
  };
  auth?: {
    authType?: number;
    bearerAuth?: {
      bearerToken?: string;
    };
    customAuth?: {
      key: string;
      value: string;
    };
  };
  // 智能任务流新格式字段
  callbackTimeout?: number;
  params?: Array<{
    type: number;
    key: string;
    value?: string;
  }>;
  returns?: Array<{
    info_id: string;
    key: string;
  }>;
  async?: boolean;
  authType?: number;
  basicAuth?: {
    basicToken?: string;
  };
  bearerAuth?: {
    bearerToken?: string;
  };
  customAuth?: {
    key: string;
    value: string;
  };
  fallbackSwitch?: number;
  inputVariables?: Array<{
    type: "string"; // 为了以后的扩展性考虑，先默认填成string
    name: string;
    placeholder: string;
    isRequired: 1 | 0; // 1:必填, 0非必填
    variableKey: string; // 从变量列表拉取，再选择
    formType: 0 | 1; // 输入方式 0代表输入，1代表选择
    chooseItemList?: string[]; // 输入方式 0代表输入，1代表选择
  }>;
  examples?: Array<{
    content: string;
    id: string;
  }>;
  infos?: Array<{
    content: string;
    id: string;
  }>;
  matchMode?: number;
  mode?: number;
  newTaskID?: number;
}>({
  branches: [],
  nodeName: "",
  intentions: [""],
  optionType: 1,
  desc: "",
  content: "",
  type: "1",
  routePolicy: "0",
  tip: "",
  inputVariables: [],
  examples: [],
  infos: [],
  matchMode: 1,
  mode: 1,
});

const requireRule = [
  {
    validator: (str) => {
      return !!trim(str);
    },
    message: t("不能为空"),
    trigger: "change",
  },
];

// 定义节点类型
const NORMAL_TASK_TYPES = [
  "startNode",
  "replyMessageNode",
  "branchNode",
  "transToHumanNode",
  "formSaveNode",
  "conditionalBranchNode",
  "interfaceInvocationNode", // 标准任务流的接口调用节点
  "transToTaskNode",
];

const AGENT_TASK_TYPES = [
  "userIntentNode",
  "infoExtractNode",
  "conditionJudgeNode",
  "interfaceInvocationNode", // 智能任务流的接口调用节点
  "endReplyNode",
];

// 判断当前是否为智能任务流（通过 nodeList 判断）
const isAgentTaskFlow = computed(() => {
  return props.nodeList.some((node) => node.type === "userIntentNode");
});

// 判断节点类型的计算属性
const isNormalTask = computed(() => {
  const nodeType = nodeCfg.value.type;

  // 如果是接口调用节点，需要根据任务流类型判断
  if (nodeType === "interfaceInvocationNode") {
    return !isAgentTaskFlow.value; // 智能任务流返回false
  }

  return NORMAL_TASK_TYPES.includes(nodeType);
});

const isAgentTask = computed(() => {
  const nodeType = nodeCfg.value.type;

  // 如果是接口调用节点，需要根据任务流类型判断
  if (nodeType === "interfaceInvocationNode") {
    return isAgentTaskFlow.value; // 智能任务流返回true
  }

  return AGENT_TASK_TYPES.includes(nodeType);
});

// 统一处理表单数据更新
const handleFormDataUpdate = ({ type, data }) => {
  switch (type) {
    case "start":
      formData.intentions = data.intentions;
      formData.desc = data.desc;
      break;
    case "reply":
      formData.content = data.content;
      formData.type = data.type;
      break;
    case "branch":
      formData.branches = data.branches;
      formData.tip = data.tip;
      formData.optionType = data.optionType;
      break;
    case "transToHuman":
      formData.routePolicy = data.routePolicy;
      formData.groupId = data.groupId;
      break;
    case "collect":
      formData.tip = data.tip;
      formData.inputVariables = data.inputVariables;
      break;
    case "conditional":
      formData.branches = data.branches;
      break;
    case "interface":
      formData.url = data.url;
      // 智能任务流使用新格式
      formData.callbackTimeout = data.callbackTimeout;
      // 过滤掉未选择的输入参数和输出参数（只在真正需要提交数据时过滤）
      if (data.params && Array.isArray(data.params)) {
        formData.params = data.params.filter((param) => {
          // 检查 key 是否存在且不为空字符串（trim 后）
          return param.key && trim(param.key);
        });
      } else {
        formData.params = data.params;
      }
      if (data.returns && Array.isArray(data.returns)) {
        formData.returns = data.returns.filter((returnItem) => {
          // 检查 key 和 variable_id 是否都存在且不为空字符串（trim 后）
          return (
            returnItem.key &&
            trim(returnItem.key) &&
            returnItem.variable_id &&
            trim(returnItem.variable_id)
          );
        });
      } else {
        formData.returns = data.returns;
      }
      formData.async = data.async;
      formData.authType = data.authType;
      formData.basicAuth = data.basicAuth;
      formData.bearerAuth = data.bearerAuth;
      formData.customAuth = data.customAuth;
      formData.fallbackSwitch = data.fallbackSwitch;
      formData.branches = data.branches;
      break;
    case "userIntention":
      formData.examples = data.examples;
      formData.desc = data.desc;
      break;
    case "infoExtract":
      formData.infos = data.infos;
      break;
    case "conditionJudge":
      formData.matchMode = data.matchMode;
      formData.branches = data.branches;
      break;
    case "endReply":
      formData.mode = data.mode;
      formData.content = data.content;
      formData.type = data.type;
      break;
    case "transTask":
      formData.newTaskID = data.newTaskID;
      break;
  }
};

const open = async (cfg: Model) => {
  // 标记正在打开节点，避免清空数据时触发 watch -> submitForm
  isOpening = true;

  // 清空 normal formData
  formData.branches = [];
  formData.nodeName = "";
  formData.intentions = [""];
  formData.desc = "";
  formData.content = "";
  formData.type = "1"; // 设置默认值
  formData.routePolicy = "0"; // 设置默认值
  formData.tip = "";
  formData.url = "";
  formData.groupId = undefined; // 清空 groupId
  formData.inputVariables = [];
  formData.fallbackSwitch = 0;
  formData.newTaskID = undefined;

  // 判断是否为智能任务流的接口调用节点
  const isAgentInterfaceNode =
    cfg.nodeData.nodeType === "interfaceInvocationNode" &&
    isAgentTaskFlow.value;

  // 只有标准任务流的接口调用节点才初始化 auth 对象（旧格式）
  if (!isAgentInterfaceNode) {
    formData.auth = {
      authType: 0,
      bearerAuth: {
        bearerToken: "",
      },
      customAuth: {
        key: "",
        value: "",
      },
    };
  } else {
    // 智能任务流使用新格式，初始化新格式字段
    formData.authType = 0;
    formData.basicAuth = { basicToken: "" };
    formData.bearerAuth = { bearerToken: "" };
    formData.customAuth = { key: "", value: "" };
    // 确保删除可能存在的旧格式 auth 对象
    delete formData.auth;
  }

  // 清空 agent formData
  formData.examples = [];

  visible.value = true;
  modelData.value = cloneDeep(cfg);
  Object.assign(formData, { ...cfg.nodeData });

  // 如果是智能任务流的接口调用节点，确保删除可能存在的旧格式 auth 对象
  if (isAgentInterfaceNode && formData.auth) {
    delete formData.auth;
  }

  if (!formData.branches.length) {
    if (nodeCfg.value.type === "branchNode") {
      formData.branches.push({
        id: getRelationUUID(),
        content: "",
      });
    }
    if (nodeCfg.value.type === "conditionalBranchNode") {
      formData.branches.push({
        id: getRelationUUID(),
        type: 1,
        content: "",
        condition: [
          {
            relation: "==",
            variableKey: "",
            value: "",
          },
        ],
      });
    }
  }
  if (
    nodeCfg.value.type === "formSaveNode" &&
    !formData.inputVariables?.length
  ) {
    formData.inputVariables = [
      {
        type: "string",
        name: "",
        placeholder: "",
        isRequired: 0, // 1:必填, 0非必填
        variableKey: "", // 从变量列表拉取，再选择
        formType: 1,
        chooseItemList: [""],
      },
    ];
  }
  await nextTick();

  // 数据加载完成，重置标记，允许后续的 formData 变化触发 submitForm
  isOpening = false;
};

const submitForm = (isClose?: boolean) => {
  const data = mergeWith(
    {},
    cloneDeep(modelData.value),
    {
      label: formatXGraphLabel(formData.nodeName),
      nodeData: cloneDeep(formData),
    },
    (source, target) => {
      if (isArray(source)) {
        return target;
      }
    },
  );
  emit("submit", data);
  if (isClose) {
    emit("close", data);
  }
};

// 标记是否正在打开节点，避免清空数据时触发 submitForm
let isOpening = false;

watch(formData, () => {
  if (isOpening) {
    return;
  }
  submitForm();
});

const handlerClose = () => {
  if (visible.value) {
    submitForm(true);
    visible.value = false;
  }
  modelData.value = {
    id: "",
    label: "",
    nodeData: {
      nodeName: "",
      nodeType: "",
      branches: [],
    },
    type: "",
  };
};

// const _handleClose = () => {
//   if (modelData.value) {
//     handlerClose();
//     modelData.value = {
//       id: "",
//       label: "",
//       nodeData: {
//         nodeName: "",
//         nodeType: "",
//         branches: [],
//       },
//       type: "",
//       x: 0,
//       y: 0,
//     };
//     formData.branches = [];
//     visible.value = false;
//   }
// };

defineExpose({
  open,
  close: handlerClose,
});
</script>
<style lang="less">
.edit-node-compo {
  // position: absolute;
  .flow-t-drawer__mask {
    background-color: transparent;
  }
  &.flow-t-drawer .flow-t-drawer__content-wrapper {
    margin: 20px 20px;
    height: calc(100% - 40px);
    border-radius: 16px;
  }
}
</style>
<style lang="less" scoped>
.header {
  padding: 20px 0px 5px 0px;
  display: flex;
  justify-content: space-between; /* 使内容分布在两端 */
  align-items: center; /* 垂直居中对齐 */
  width: 100%;
  .title-editor {
    margin: 0 10px;
    font-size: 16px;
    flex: 1;
    align-self: center;
    .label {
      color: #6d778c;
      white-space: nowrap;
    }
  }
}

.title-operation {
  cursor: pointer; /* 鼠标悬停时显示手形光标 */
}
</style>
